import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getStaff, 
  getDailyBalances, 
  getDailyTransactions, 
  setOpeningBalance,
  initializeDailyBalance,
  getStaffDailyReport,
  getAgencyDailyReport
} from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  CalendarBlank, 
  Warning, 
  CheckCircle, 
  ArrowUp, 
  ArrowDown,
  ShoppingCart,
  ArrowCounterClockwise,
  CurrencyInr,
  Users,
  Buildings
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';

export default function DailyBalancePage() {
  const { user } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [dailyBalances, setDailyBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [staffReport, setStaffReport] = useState([]);
  const [agencyReport, setAgencyReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [useRange, setUseRange] = useState(false);
  const [openingDialogOpen, setOpeningDialogOpen] = useState(false);
  const [openingFormData, setOpeningFormData] = useState({ staff_id: '', opening_balance: '' });

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdmin = isOwner || user?.role === 'assistant';
  const isAccountant = user?.role === 'accountant';
  const isAllowed = isOwner || isAccountant;
  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const startDateStr = dateRange.start ? format(dateRange.start, 'yyyy-MM-dd') : null;
  const endDateStr = dateRange.end ? format(dateRange.end, 'yyyy-MM-dd') : null;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      
      const params = useRange && startDateStr && endDateStr
        ? { staffId, startDate: startDateStr, endDate: endDateStr }
        : { staffId, date: dateStr };
      
      const [balancesRes, transactionsRes] = await Promise.all([
        getDailyBalances(params.staffId, params.date, params.startDate, params.endDate),
        getDailyTransactions(params.staffId, params.date, params.startDate, params.endDate)
      ]);
      
      setDailyBalances(balancesRes.data);
      setTransactions(transactionsRes.data);
      
      // Fetch reports
      const [staffReportRes, agencyReportRes] = await Promise.all([
        getStaffDailyReport(params.date, params.startDate, params.endDate),
        getAgencyDailyReport(params.date, params.startDate, params.endDate)
      ]);
      setStaffReport(staffReportRes.data);
      setAgencyReport(agencyReportRes.data);
      
      if (isAdmin) {
        const staffRes = await getStaff();
        setStaffList(staffRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedStaff, dateStr, useRange, startDateStr, endDateStr]);

  useEffect(() => {
    if (isAllowed) fetchData();
  }, [fetchData, isAllowed]);

  const handleSetOpening = async (e) => {
    e.preventDefault();
    if (!openingFormData.staff_id) {
      toast.error('Please select a staff member');
      return;
    }
    try {
      await setOpeningBalance(openingFormData.staff_id, parseFloat(openingFormData.opening_balance), dateStr);
      toast.success('Opening balance set successfully');
      setOpeningDialogOpen(false);
      setOpeningFormData({ staff_id: '', opening_balance: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set opening balance');
    }
  };

  const handleInitializeDaily = async (staffId) => {
    try {
      await initializeDailyBalance(staffId, dateStr);
      toast.success('Daily balance initialized');
      fetchData();
    } catch (error) {
      toast.error('Failed to initialize');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'booking': return <ShoppingCart size={16} className="text-red-500" />;
      case 'refund': return <ArrowCounterClockwise size={16} className="text-green-500" />;
      case 'fund_add': return <ArrowUp size={16} className="text-green-600" />;
      case 'fund_deduct': return <ArrowDown size={16} className="text-red-600" />;
      case 'opening': return <CalendarBlank size={16} className="text-blue-500" />;
      default: return <CurrencyInr size={16} className="text-gray-500" />;
    }
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'booking': return 'Booking';
      case 'refund': return 'Refund';
      case 'fund_add': return 'Funds Added';
      case 'fund_deduct': return 'Funds Deducted';
      case 'opening': return 'Opening Set';
      default: return type;
    }
  };

  // Check for mismatches
  const mismatchedBalances = dailyBalances.filter(b => !b.is_balanced);

  if (!isAdmin) {
    return <div className="p-4 text-red-600">Access denied. Admin only.</div>;
  }

  if (!isAllowed) return <div className="p-6 text-center text-red-600 font-medium" data-testid="daily-balance-access-denied">Access denied. Only Admin or CA can view Daily Balance.</div>;

  return (
    <div className="space-y-6" data-testid="daily-balance-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Daily Balance Tracking</h1>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[200px] justify-start text-left" data-testid="date-picker-btn">
                <CalendarBlank size={18} className="mr-2" />
                {format(selectedDate, 'dd MMM yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* Staff Filter */}
          <SearchableSelect
            options={[
              { value: 'all', label: 'All Staff' },
              ...staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))
            ]}
            value={selectedStaff}
            onValueChange={setSelectedStaff}
            placeholder="Select Staff"
            searchPlaceholder="Search staff..."
            className="w-48"
            data-testid="staff-filter-daily"
          />

          {/* Set Opening Balance */}
          <Dialog open={openingDialogOpen} onOpenChange={setOpeningDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="set-opening-btn">
                Set Opening Balance
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Set Opening Balance for {format(selectedDate, 'dd MMM yyyy')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSetOpening} className="space-y-4">
                <div>
                  <Label className="form-label">Staff</Label>
                  <SearchableSelect
                    options={staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))}
                    value={openingFormData.staff_id}
                    onValueChange={(v) => setOpeningFormData({ ...openingFormData, staff_id: v })}
                    placeholder="Select Staff"
                    searchPlaceholder="Search staff..."
                    data-testid="opening-staff-select"
                  />
                </div>
                <div>
                  <Label className="form-label">Opening Balance (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={openingFormData.opening_balance}
                    onChange={(e) => setOpeningFormData({ ...openingFormData, opening_balance: e.target.value })}
                    className="form-input"
                    placeholder="Enter opening balance"
                    required
                    data-testid="opening-amount-input"
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="opening-submit-btn">
                  Set Opening Balance
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Mismatch Alert */}
      {mismatchedBalances.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 flex items-start gap-3" data-testid="mismatch-alert">
          <Warning size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-800">Balance Mismatch Detected!</h3>
            <p className="text-sm text-red-700">
              {mismatchedBalances.length} staff member(s) have balance mismatch. Check closing balance calculations.
            </p>
            <ul className="mt-2 text-sm text-red-600">
              {mismatchedBalances.map(b => (
                <li key={b.id}>• {b.staff_name}: Mismatch of {formatCurrency(b.mismatch)}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Tabs defaultValue="daily" className="w-full">
        <TabsList>
          <TabsTrigger value="daily">Daily Balances</TabsTrigger>
          <TabsTrigger value="transactions">Transaction History</TabsTrigger>
          <TabsTrigger value="staff-report">Staff Report</TabsTrigger>
          <TabsTrigger value="agency-report">Agency Report</TabsTrigger>
        </TabsList>

        {/* Daily Balances Tab */}
        <TabsContent value="daily" className="mt-4">
          <div className="card-flat overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Opening</TableHead>
                    <TableHead className="text-right">Funds Added</TableHead>
                    <TableHead className="text-right">Bookings</TableHead>
                    <TableHead className="text-right">Refunds</TableHead>
                    <TableHead className="text-right">Closing</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyBalances.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-gray-500">
                        No daily balance records for {format(selectedDate, 'dd MMM yyyy')}
                        <br />
                        <Button 
                          variant="link" 
                          className="text-[#002FA7]"
                          onClick={() => staffList.forEach(s => handleInitializeDaily(s.id))}
                        >
                          Initialize for all staff
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    dailyBalances.map((b) => (
                      <TableRow key={b.id} className={!b.is_balanced ? 'bg-red-50' : ''} data-testid={`daily-row-${b.id}`}>
                        <TableCell className="font-medium">{b.staff_name}</TableCell>
                        <TableCell>{b.date}</TableCell>
                        <TableCell className="numeric">{formatCurrency(b.opening_balance)}</TableCell>
                        <TableCell className="numeric text-green-600">+{formatCurrency(b.funds_added)}</TableCell>
                        <TableCell className="numeric text-red-600">-{formatCurrency(b.total_bookings)}</TableCell>
                        <TableCell className="numeric text-green-600">+{formatCurrency(b.total_refunds)}</TableCell>
                        <TableCell className="numeric font-semibold">{formatCurrency(b.closing_balance)}</TableCell>
                        <TableCell className="numeric text-gray-500">{formatCurrency(b.expected_closing)}</TableCell>
                        <TableCell>
                          {b.is_balanced ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle size={18} /> Balanced
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600">
                              <Warning size={18} /> Mismatch: {formatCurrency(b.mismatch)}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Formula Explanation */}
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-2">Balance Formula</h3>
            <p className="text-sm text-gray-600 font-mono">
              Closing = Opening + Funds Added - Bookings + Refunds
            </p>
          </div>
        </TabsContent>

        {/* Transaction History Tab */}
        <TabsContent value="transactions" className="mt-4">
          <div className="card-flat overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date/Time</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Balance After</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-500">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((t) => (
                      <TableRow key={t.id} data-testid={`transaction-row-${t.id}`}>
                        <TableCell className="text-sm">{formatDateTime(t.created_at)}</TableCell>
                        <TableCell className="font-medium">{t.staff_name}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-2">
                            {getTransactionIcon(t.transaction_type)}
                            {getTransactionLabel(t.transaction_type)}
                          </span>
                        </TableCell>
                        <TableCell className={`numeric ${t.transaction_type === 'booking' || t.transaction_type === 'fund_deduct' ? 'text-red-600' : 'text-green-600'}`}>
                          {t.transaction_type === 'booking' || t.transaction_type === 'fund_deduct' ? '-' : '+'}
                          {formatCurrency(t.amount)}
                        </TableCell>
                        <TableCell>
                          {t.source === 'card' && <span className="badge-tataneu">Card</span>}
                          {t.source === 'amazon' && <span className="badge-amazon">Amazon</span>}
                          {!t.source && '-'}
                        </TableCell>
                        <TableCell className="text-gray-600 max-w-[200px] truncate">{t.description}</TableCell>
                        <TableCell className="numeric font-semibold">{formatCurrency(t.balance_after)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Staff Report Tab */}
        <TabsContent value="staff-report" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <div className="flex items-center gap-2 mb-4 pb-4 border-b">
              <Users size={24} className="text-[#002FA7]" />
              <h2 className="text-lg font-semibold">Staff-wise Daily Report - {format(selectedDate, 'dd MMM yyyy')}</h2>
            </div>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Opening</TableHead>
                    <TableHead className="text-right">Funds Added</TableHead>
                    <TableHead className="text-right">Total Bookings</TableHead>
                    <TableHead className="text-right">Total Refunds</TableHead>
                    <TableHead className="text-right">Closing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffReport.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-500">No data</TableCell>
                    </TableRow>
                  ) : (
                    staffReport.map((r) => (
                      <TableRow key={r.staff_id}>
                        <TableCell className="font-medium">{r.staff_name}</TableCell>
                        <TableCell className="text-gray-500">{r.email}</TableCell>
                        <TableCell className="numeric">{formatCurrency(r.opening_balance)}</TableCell>
                        <TableCell className="numeric text-green-600">+{formatCurrency(r.funds_added)}</TableCell>
                        <TableCell className="numeric text-red-600">-{formatCurrency(r.total_bookings)}</TableCell>
                        <TableCell className="numeric text-green-600">+{formatCurrency(r.total_refunds)}</TableCell>
                        <TableCell className="numeric font-semibold">{formatCurrency(r.closing_balance)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Agency Report Tab */}
        <TabsContent value="agency-report" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <div className="flex items-center gap-2 mb-4 pb-4 border-b">
              <Buildings size={24} className="text-[#002FA7]" />
              <h2 className="text-lg font-semibold">Agency-wise Daily Report - {format(selectedDate, 'dd MMM yyyy')}</h2>
            </div>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Agency</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Total Bookings</TableHead>
                    <TableHead className="text-right">Completed</TableHead>
                    <TableHead className="text-right">Refunded</TableHead>
                    <TableHead className="text-right">Total Qty</TableHead>
                    <TableHead className="text-right">Total Amount</TableHead>
                    <TableHead className="text-right">Total Fee</TableHead>
                    <TableHead className="text-right">Grand Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agencyReport.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-gray-500">No data</TableCell>
                    </TableRow>
                  ) : (
                    agencyReport.map((r) => (
                      <TableRow key={r.agency_id}>
                        <TableCell className="font-medium">{r.agency_name}</TableCell>
                        <TableCell className="numeric">{formatCurrency(r.rate)}</TableCell>
                        <TableCell className="numeric">{r.total_bookings}</TableCell>
                        <TableCell className="numeric text-green-600">{r.completed_bookings}</TableCell>
                        <TableCell className="numeric text-orange-600">{r.refunded_bookings}</TableCell>
                        <TableCell className="numeric font-semibold">{r.total_quantity}</TableCell>
                        <TableCell className="numeric">{formatCurrency(r.total_amount)}</TableCell>
                        <TableCell className="numeric text-[#E90069]">{formatCurrency(r.total_fee)}</TableCell>
                        <TableCell className="numeric font-semibold">{formatCurrency(r.total_with_fee)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
